import axios from "axios";
import { BASE_URL } from "../constants/APIConstants";
import { apiRequest, logApiError } from "../api/http";
import { encryptPayload, decryptPayload } from "../utils/crypto";
import { SESSION_EVENT } from "../utils/roles";

// Except `sendEmailVerification` and `changeOwnPassword`, these endpoints are
// called before a token exists, so they don't use the authenticated client.

const MUST_CHANGE_KEY = "mustChangePassword";

/** Temporary password (new account, reset): the admin must choose a password before anything else. */
export const mustChangePassword = () => localStorage.getItem(MUST_CHANGE_KEY) === "1";
export function setMustChangePassword(value) {
  if (value) localStorage.setItem(MUST_CHANGE_KEY, "1");
  else localStorage.removeItem(MUST_CHANGE_KEY);
}

/**
 * Changes the password of the logged-in admin. The API closes every other
 * session and returns a new token, stored here. Rejects with the API error.
 */
export async function changeOwnPassword(currentPassword, newPassword) {
  const data = await apiRequest("post", "/api/admin/me/password", {
    data: { currentPassword, newPassword },
    rethrow: true,
  });
  const { token } = decryptPayload(data.data);
  localStorage.setItem("token", token);
  setMustChangePassword(false);
  return true;
}

/**
 * What the browser can tell about the computer used to sign in (system, browser,
 * screen, time zone, language), sent with the credentials: the API emails it to
 * the support with the IP address and the place (security of the back-office).
 */
export async function clientInfo() {
  const nav = typeof navigator !== "undefined" ? navigator : {};
  const info = {
    language: nav.language,
    timezone: (() => {
      try {
        return Intl.DateTimeFormat().resolvedOptions().timeZone;
      } catch (error) {
        return undefined;
      }
    })(),
    screen: typeof window !== "undefined" && window.screen ? `${window.screen.width}×${window.screen.height}` : undefined,
    pixelRatio: typeof window !== "undefined" ? window.devicePixelRatio : undefined,
    cores: nav.hardwareConcurrency,
    memory: nav.deviceMemory,
    touch: typeof nav.maxTouchPoints === "number" ? nav.maxTouchPoints > 0 : undefined,
  };
  try {
    if (nav.userAgentData?.getHighEntropyValues) {
      const h = await nav.userAgentData.getHighEntropyValues(["platform", "platformVersion", "model", "architecture", "fullVersionList"]);
      info.hints = {
        platform: h.platform,
        platformVersion: h.platformVersion,
        model: h.model || undefined,
        architecture: h.architecture,
        mobile: nav.userAgentData.mobile,
        browsers: (h.fullVersionList || [])
          .filter((b) => !/not.?a.?brand|chromium/i.test(b.brand))
          .map((b) => `${b.brand} ${b.version.split(".")[0]}`)
          .slice(0, 2),
      };
    }
  } catch (error) {
    // Not available (Safari, Firefox): the user agent is enough
  }
  return info;
}

/** Logs in and stores the token. Rejects (without reason) on failure. */
export async function postLoginData(obj) {
  const client = await clientInfo();
  return axios
    .post(`${BASE_URL}/api/admin/login`, encryptPayload({ ...obj, client }))
    .then((response) => {
      const decrypt = decryptPayload(response.data.data);
      localStorage.setItem("token", decrypt.token);
      setMustChangePassword(decrypt.mustChangePassword);
    })
    .catch((error) => {
      console.error(error);
      return Promise.reject();
    });
}

/** Checks the credentials without touching the stored token. Rejects with `false` on failure. */
export function confirmPassword(obj) {
  // Not a new sign-in: no email to the support
  return axios
    .post(`${BASE_URL}/api/admin/login`, encryptPayload({ ...obj, purpose: "confirm" }))
    .then(() => true)
    .catch((error) => {
      console.log(error);
      return Promise.reject(false);
    });
}

/**
 * Validates the stored token and caches the decrypted session in localStorage.
 * Resolves `true` when valid; clears the token and resolves `false` otherwise.
 */
export async function authSession() {
  const token = localStorage.getItem("token");
  if (!token) return false;

  try {
    const response = await axios.get(`${BASE_URL}/api/admin/auth/${token}`);
    const session = decryptPayload(response.data.data);
    localStorage.setItem("session", JSON.stringify(session));
    setMustChangePassword(session.mustChangePassword);
    window.dispatchEvent(new Event(SESSION_EVENT));
    return true;
  } catch (error) {
    console.error(error);
    // Network error: keep the session; rejected token (expired, deactivated): log out.
    if (!error?.response) return true;
    localStorage.removeItem("token");
    return false;
  }
}

export function sendFACode(obj) {
  return axios
    .post(`${BASE_URL}/api/user/sendCode`, obj)
    .then((response) => {
      localStorage.setItem("2fatoken", response.data.token);
    })
    .catch(logApiError);
}

/** Resolves `true` when the code is valid, rejects with `false` otherwise. */
export function verifyFACode(obj) {
  return axios
    .post(`${BASE_URL}/api/user/verifyCode`, obj)
    .then(() => true)
    .catch(() => Promise.reject(false));
}

/** Authenticated: sends an email verification code to the logged-in admin. */
export function sendEmailVerification(obj) {
  return apiRequest("post", "/api/user/sendMailCode", {
    data: obj,
    select: (data) => {
      localStorage.setItem("email verification token", data.token);
    },
  });
}
